import { DownloadIcon } from 'lucide-react'
import { memo, type ReactNode, useMemo } from 'react'

import type {
  DataTableColumn,
  DataTableFilter,
  DataTablePagination,
} from '@/components/data-table/data-table.interface'
import { DataTable } from '@/components/data-table/data-table.organism'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type {
  BillingInvoice,
  BillingTableBaseProps,
  BillingTableSortFilterProps,
} from './billing.interface'
import { BillingInvoiceStatusBadge } from './billing-invoice-status-badge.atom'

export interface BillingInvoicesTableProps
  extends BillingTableSortFilterProps,
    BillingTableBaseProps {
  data: BillingInvoice[]
  pagination: DataTablePagination
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onDownload: (downloadUrl: string) => void
}

const formatDate = (date: Date): string =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const formatCurrency = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)

const statusFilterList: DataTableFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'multi-select',
    optionList: [
      { label: 'Draft', value: 'draft' },
      { label: 'Open', value: 'open' },
      { label: 'Paid', value: 'paid' },
      { label: 'Void', value: 'void' },
      { label: 'Uncollectible', value: 'uncollectible' },
    ],
  },
]

const getRowId = (invoice: BillingInvoice): string => invoice.id

type DownloadHandler = (downloadUrl: string) => void

const dateColumn: DataTableColumn<BillingInvoice> = {
  id: 'createdAt',
  header: 'Date',
  isSortable: true,
  width: '120px',
  cell: (invoice) => (
    <span className="text-sm text-muted-foreground">{formatDate(invoice.createdAt)}</span>
  ),
}

const numberColumn: DataTableColumn<BillingInvoice> = {
  id: 'number',
  header: 'Invoice #',
  width: '150px',
  cell: (invoice) => <span className="font-medium">{invoice.number ?? '—'}</span>,
}

const descriptionColumn: DataTableColumn<BillingInvoice> = {
  id: 'description',
  header: 'Description',
  cell: (invoice) => (
    <span
      className="text-sm text-muted-foreground truncate"
      title={invoice.description ?? undefined}
    >
      {invoice.description ?? '—'}
    </span>
  ),
}

const statusColumn: DataTableColumn<BillingInvoice> = {
  id: 'status',
  header: 'Status',
  width: '120px',
  cell: (invoice) => <BillingInvoiceStatusBadge status={invoice.status} />,
}

const amountColumn: DataTableColumn<BillingInvoice> = {
  id: 'amountDue',
  header: 'Amount',
  isSortable: true,
  width: '120px',
  align: 'right',
  cell: (invoice) => (
    <span className="font-medium tabular-nums">
      {formatCurrency(invoice.amountDue, invoice.currency)}
    </span>
  ),
}

const createActionsColumn = (onDownload: DownloadHandler): DataTableColumn<BillingInvoice> => ({
  id: 'actions',
  header: '',
  width: '80px',
  align: 'right',
  cell: (invoice) =>
    invoice.downloadUrl ? (
      <Button
        variant="ghost"
        size="sm"
        className="billing-invoice-download-button h-8 px-2"
        onClick={() => onDownload(invoice.downloadUrl as string)}
      >
        <DownloadIcon className="size-4" />
        <span className="sr-only">Download PDF</span>
      </Button>
    ) : null,
})

const createColumnList = (onDownload: DownloadHandler): DataTableColumn<BillingInvoice>[] => [
  dateColumn,
  numberColumn,
  descriptionColumn,
  statusColumn,
  amountColumn,
  createActionsColumn(onDownload),
]

const BillingInvoicesTableBase: React.FC<BillingInvoicesTableProps> = ({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onDownload,
  sortBy,
  sortOrder,
  onSortChange,
  filterValueList,
  onFilterChange,
  isLoading = false,
  className,
}): ReactNode => {
  const columnList = useMemo(() => createColumnList(onDownload), [onDownload])

  return (
    <DataTable
      data={data}
      columnList={columnList}
      getRowId={getRowId}
      pagination={pagination}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      filterList={onFilterChange ? statusFilterList : undefined}
      filterValueList={filterValueList}
      onFilterChange={onFilterChange}
      isLoading={isLoading}
      emptyMessage="No invoices found. Your invoices will appear here once generated."
      className={cn('billing-invoices-table', className)}
    />
  )
}

export const BillingInvoicesTable = memo(BillingInvoicesTableBase)

BillingInvoicesTable.displayName = 'BillingInvoicesTable'
